import { useState } from "react";
import { API } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { FileSpreadsheet, FileText, Download, Users, CalendarDays, Wallet, ClockIcon } from "lucide-react";

function download(url, filename) {
  const token = localStorage.getItem("access_token");
  fetch(url, { headers: { Authorization: `Bearer ${token}` }, credentials: "include" })
    .then(r => { if (!r.ok) throw new Error("Download failed (" + r.status + ")"); return r.blob(); })
    .then(blob => {
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`Downloaded ${filename}`);
    })
    .catch(e => toast.error(e.message));
}

const REPORTS = [
  {
    id: "attendance",
    title: "Attendance Report",
    desc: "Daily check-ins, working hours and lateness for a selected month.",
    icon: ClockIcon,
    tone: "text-[#DC2626] bg-red-50",
    needsMonth: true,
    excel: (ym) => ({ url: `${API}/reports/attendance.xlsx?year=${ym.split("-")[0]}&month=${Number(ym.split("-")[1])}`, name: `attendance-${ym}.xlsx` }),
    pdf: (ym) => ({ url: `${API}/reports/attendance.pdf?year=${ym.split("-")[0]}&month=${Number(ym.split("-")[1])}`, name: `attendance-${ym}.pdf` }),
  },
  {
    id: "leave",
    title: "Leave Report",
    desc: "Every leave request with type, status, days and reviewer.",
    icon: CalendarDays,
    tone: "text-orange-600 bg-orange-50",
    excel: () => ({ url: `${API}/reports/leave.xlsx`, name: "leave-report.xlsx" }),
    pdf: () => ({ url: `${API}/reports/leave.pdf`, name: "leave-report.pdf" }),
  },
  {
    id: "payroll",
    title: "Payroll Register",
    desc: "Consolidated gross, deductions and net pay for a month.",
    icon: Wallet,
    tone: "text-emerald-600 bg-emerald-50",
    needsMonth: true,
    excel: (ym) => ({ url: `${API}/reports/payroll.xlsx?month=${ym}`, name: `payroll-${ym}.xlsx` }),
  },
  {
    id: "employees",
    title: "Employee Master",
    desc: "Full employee directory with department, joining date and manager.",
    icon: Users,
    tone: "text-slate-700 bg-slate-100",
    excel: () => ({ url: `${API}/reports/employees.xlsx`, name: "employees.xlsx" }),
  },
];

export default function Reports() {
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));

  return (
    <div className="space-y-6" data-testid="reports-root">
      <div>
        <div className="text-xs uppercase font-semibold tracking-[0.1em] text-slate-500">Reports</div>
        <h1 className="mt-1 font-display text-3xl font-extrabold tracking-tight text-slate-900">Exports for leadership</h1>
        <p className="mt-1 text-sm text-slate-500">One-click Excel and PDF — email them straight to your CEO.</p>
      </div>

      <Card className="p-5 rounded-xl border-slate-200/60 flex flex-wrap items-end gap-4">
        <div>
          <Label className="text-xs">Report period (for attendance/payroll)</Label>
          <Input type="month" value={month} onChange={e => setMonth(e.target.value)} className="w-48 mt-1" data-testid="report-month" />
        </div>
        <div className="text-xs text-slate-500 pb-2">
          Leave and Employee reports use the full history.
        </div>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {REPORTS.map(r => {
          const Icon = r.icon;
          return (
            <Card key={r.id} className="p-6 rounded-xl border-slate-200/60" data-testid={`report-card-${r.id}`}>
              <div className="flex items-start gap-4">
                <div className={`h-12 w-12 rounded-lg grid place-items-center shrink-0 ${r.tone}`}>
                  <Icon className="h-6 w-6" strokeWidth={1.5} />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-display text-lg font-semibold text-slate-900">{r.title}</h3>
                  <p className="text-sm text-slate-500 mt-0.5">{r.desc}</p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {r.excel && (
                      <Button variant="outline" size="sm" onClick={() => {
                        const { url, name } = r.excel(month);
                        download(url, name);
                      }} data-testid={`download-${r.id}-xlsx`}>
                        <FileSpreadsheet className="h-4 w-4 mr-1.5 text-emerald-600" strokeWidth={1.8} />
                        Excel
                      </Button>
                    )}
                    {r.pdf && (
                      <Button variant="outline" size="sm" onClick={() => {
                        const { url, name } = r.pdf(month);
                        download(url, name);
                      }} data-testid={`download-${r.id}-pdf`}>
                        <FileText className="h-4 w-4 mr-1.5 text-red-600" strokeWidth={1.8} />
                        PDF
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
